import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import {
  FaBrain, FaCouch, FaHandshake, FaUsers, FaPodcast, FaSitemap, FaFileAlt, 
  FaUser, FaHome, FaArrowLeft, FaFilter, FaTimes, FaExternalLinkAlt, 
  FaGraduationCap, FaCoffee, FaCopy, FaCheckCircle, FaSortAlphaDown, 
  FaSortAlphaUp, FaCalendarAlt, FaClock
} from 'react-icons/fa';
import { usePodcastReset, usePodcastManager } from '../hooks/usePodcastManager';
import SearchInput from '../components/SearchInput';
import { personalityArticles } from '../data/Personality';
import { collaborativeArticles } from '../data/Collaborative';
import { psychotherapyArticles } from '../data/Psychotherapy';
import { familyArticles } from '../data/Family';

// ✅ DATA: Articoli importati dai moduli esterni
const articlesByTopic = {
  1: personalityArticles,
  2: collaborativeArticles,
  3: psychotherapyArticles,
  4: familyArticles
};

// ✅ FUNZIONI HELPER
const getAllArticles = () => {
  const allArticles = [];
  Object.values(articlesByTopic).forEach(topicArticles => {
    allArticles.push(...topicArticles);
  });
  return allArticles;
};

const topics = [
  {
    id: 1,
    titleKey: "personalityAssessment",
    descriptionKey: "personalityAssessmentDesc",
    icon: FaBrain,
    articlesCount: 8,
    color: "bg-blue-500"
  },
  {
    id: 2,
    titleKey: "collaborativeAssessment",
    descriptionKey: "collaborativeAssessmentDesc",
    icon: FaHandshake,
    articlesCount: 4,
    color: "bg-green-500"
  },
  {
    id: 3,
    titleKey: "psychotherapy",
    descriptionKey: "psychotherapyDesc",
    icon: FaCouch,
    articlesCount: 8, // ✅ AGGIORNATO (erano 7, aggiunto un nuovo articolo)
    color: "bg-purple-500"
  },
  {
    id: 4,
    titleKey: "familyTherapy",
    descriptionKey: "familyTherapyDesc",
    icon: FaUsers,
    articlesCount: 20,
    color: "bg-red-500"
  }
];

// ✅ COMPONENTI INTERNI
const TopicCard = ({ topic, onSelect }) => {
  const { t } = useLanguage();

  const getLanguageCounts = () => {
    const articles = articlesByTopic[topic.id] || [];
    return {
      en: articles.filter(article => {
        if (!article.availableLanguages.includes('en')) return false;
        const resources = article.resources?.en;
        return resources && (resources.blogpost || resources.mindmap || resources.podcast);
      }).length,
      it: articles.filter(article => {
        if (!article.availableLanguages.includes('it')) return false;
        const resources = article.resources?.it;
        return resources && (resources.blogpost || resources.mindmap || resources.podcast);
      }).length
    };
  };

  const languageCounts = getLanguageCounts();

  return (
    <motion.div
      whileHover={{ scale: 1.05 }}
      className="bg-white rounded-lg shadow-lg p-8 text-center cursor-pointer relative"
      onClick={() => onSelect(topic)}
    >
      <div className="absolute top-4 right-4 flex space-x-2">
        {languageCounts.en > 0 && (
          <div className="flex items-center px-2 py-1 bg-blue-50 rounded-full border border-blue-200">
            <span className="mr-1 text-sm">🇺🇸</span>
            <span className="text-xs font-medium text-blue-800">{languageCounts.en}</span>
          </div>
        )}
        {languageCounts.it > 0 && (
          <div className="flex items-center px-2 py-1 bg-green-50 rounded-full border border-green-200">
            <span className="mr-1 text-sm">🇮🇹</span>
            <span className="text-xs font-medium text-green-800">{languageCounts.it}</span>
          </div>
        )}
      </div>

      <div className={`w-20 h-20 ${topic.color} rounded-full flex items-center justify-center mb-6 mx-auto`}>
        <topic.icon className="text-white text-3xl" />
      </div>
      <h3 className="text-2xl font-bold text-gray-900 mb-4">{t(topic.titleKey)}</h3>
      <p className="text-gray-600 text-lg mb-4">{topic.articlesCount} {t('articles')}</p>
      <div className="text-sm text-gray-500">
        {t(topic.descriptionKey)}
      </div>
    </motion.div>
  );
};

const LanguageFilter = ({ selectedFilter, onFilterChange, articleCounts }) => {
  const { t } = useLanguage();
  
  const filters = [
    { key: 'all', label: t('allLanguages'), count: articleCounts.total, flag: '🌐' },
    { key: 'en', label: t('englishOnly'), count: articleCounts.en, flag: '🇺🇸' },
    { key: 'it', label: t('italianOnly'), count: articleCounts.it, flag: '🇮🇹' }
  ];

  return (
    <div className="bg-white rounded-lg shadow-md p-4 mb-6">
      <div className="flex items-center mb-3">
        <FaFilter className="text-gray-600 mr-2" />
        <h3 className="text-lg font-semibold text-gray-900">{t('filterByLanguage')}</h3>
      </div>
      <div className="flex flex-wrap gap-2">
        {filters.map(filter => (
          <button
            key={filter.key}
            onClick={() => onFilterChange(filter.key)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-full text-sm font-medium transition-colors
              ${selectedFilter === filter.key 
                ? 'bg-indigo-600 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            <span>{filter.flag}</span>
            <span>{filter.label}</span>
            <span className={`px-2 py-1 rounded-full text-xs
              ${selectedFilter === filter.key 
                ? 'bg-white text-indigo-600' 
                : 'bg-gray-200 text-gray-600'}`}>
              {filter.count}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

const SortFilter = ({ selectedSort, onSortChange }) => {
  const { t, language } = useLanguage();
  
  const sortOptions = [
    { key: 'alphabetical-asc', label: language === 'en' ? 'Alphabetical A-Z' : 'Alfabetico A-Z', icon: FaSortAlphaDown, flag: '📝' },
    { key: 'alphabetical-desc', label: language === 'en' ? 'Alphabetical Z-A' : 'Alfabetico Z-A', icon: FaSortAlphaUp, flag: '📝' },
    { key: 'date-newest', label: language === 'en' ? 'Newest First' : 'Più Recenti', icon: FaCalendarAlt, flag: '📅' },
    { key: 'date-oldest', label: language === 'en' ? 'Oldest First' : 'Più Vecchi', icon: FaClock, flag: '📅' }
  ];

  return (
    <div className="bg-white rounded-lg shadow-md p-4 mb-6">
      <div className="flex items-center mb-3">
        <FaSortAlphaDown className="text-gray-600 mr-2" />
        <h3 className="text-lg font-semibold text-gray-900">
          {language === 'en' ? 'Sort Articles' : 'Ordina Articoli'}
        </h3>
      </div>
      <div className="flex flex-wrap gap-2">
        {sortOptions.map(option => (
          <button
            key={option.key}
            onClick={() => onSortChange(option.key)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-full text-sm font-medium transition-colors
              ${selectedSort === option.key 
                ? 'bg-purple-600 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            <span>{option.flag}</span>
            <option.icon className="text-sm" />
            <span>{option.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

const MindMapFallbackModal = ({ isOpen, onClose, url, title, language }) => {
  const { t } = useLanguage();
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy link:', error);
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (fallbackError) {
        console.error('Fallback copy failed:', fallbackError);
      }
      document.body.removeChild(textArea);
    }
  };

  const handleTryAgain = () => {
    onClose();
    setTimeout(() => {
      window.open(url, '_blank', 'noopener,noreferrer');
    }, 100);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900">
            {language === 'en' ? 'Mind Map Access' : 'Accesso Mappa Mentale'}
          </h3>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-full transition-colors"
          >
            <FaTimes size={16} />
          </button>
        </div>

        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FaSitemap className="text-blue-600 text-2xl" />
          </div>
          <h4 className="font-semibold text-gray-900 mb-2">{title}</h4>
          <p className="text-sm text-gray-600">
            {language === 'en' 
              ? 'Mind maps open in a new window for the best viewing experience. If it didn\'t open automatically, try the options below:'
              : 'Le mappe mentali si aprono in una nuova finestra per la migliore esperienza di visualizzazione. Se non si è aperta automaticamente, prova le opzioni qui sotto:'}
          </p>
        </div>

        <div className="space-y-3">
          <button
            onClick={handleTryAgain}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            <FaExternalLinkAlt />
            {language === 'en' ? 'Try Opening Again' : 'Riprova ad Aprire'}
          </button>
          
          <button
            onClick={handleCopyLink}
            className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-colors
              ${copied 
                ? 'bg-green-100 text-green-800 border border-green-300' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'}`}
          >
            {copied ? <FaCheckCircle /> : <FaCopy />}
            {copied 
              ? (language === 'en' ? 'Link Copied!' : 'Link Copiato!') 
              : (language === 'en' ? 'Copy Link' : 'Copia Link')}
          </button>
        </div>

        <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-xs text-amber-800">
            <strong>{language === 'en' ? 'Tip:' : 'Suggerimento:'}</strong>{' '}
            {language === 'en' 
              ? 'If popups are blocked, copy the link and paste it in a new browser tab.'
              : 'Se i popup sono bloccati, copia il link e incollalo in una nuova scheda del browser.'}
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
};

const ResourceModal = ({ isOpen, onClose, resource, language }) => {
  const { t } = useLanguage();

  if (!isOpen) return null;

  const renderContent = () => {
    switch (resource.type) {
      case 'ebook':
        return (
          <div className="w-full h-full">
            <iframe
              src={resource.url}
              className="w-full h-full border-0 rounded-lg"
              title={`Blog Post: ${resource.title}`}
            />
          </div>
        );
      case 'blogpost':
        return (
          <div className="w-full h-full">
            <iframe
              src={resource.url}
              className="w-full h-full border-0 rounded-lg"
              title={`Blog Post: ${resource.title}`}
            />
          </div>
        );
      case 'podcast':
        return (
          <div className="w-full h-full flex items-center justify-center bg-gray-50">
            <div className="max-w-xs w-full">
              <iframe 
                src={resource.url}
                style={{
                  outline: 'none',
                  border: 'none',
                  width: '100%',
                  borderRadius: '20px',
                  height: '625px',
                  maxWidth: '237px'
                }}
                scrolling="no"
                title={`Podcast: ${resource.title}`}
              />
            </div>
          </div>
        );
      default:
        return <div>Content not available</div>;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
        className="relative w-full h-full max-w-6xl max-h-[90vh] bg-white rounded-lg shadow-xl overflow-hidden m-4"
        onClick={(e) => e.stopPropagation()}
        data-resource-modal={resource.type}
      >
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-bold text-gray-900">
            {(resource.type === 'ebook' || resource.type === 'blogpost') 
              ? (language === 'en' ? 'Read the blog' : 'Leggi il blog') 
              : resource.type === 'podcast' ? t('listenPodcast') : t('readBlogPost')}
            {language === 'en' ? ' (English)' : ' (Italiano)'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
          >
            <FaTimes size={20} />
          </button>
        </div>
        <div className="h-full pb-16">
          {renderContent()}
        </div>
      </motion.div>
    </motion.div>
  );
};

// ✅ COMPLETAMENTE RIDISEGNATO: ArticleCard che corrisponde all'immagine
const ArticleCard = ({ article }) => {
  const { t, language } = useLanguage();
  const [modalResource, setModalResource] = useState(null);
  const [showMindMapFallback, setShowMindMapFallback] = useState(false);
  const [fallbackMindMapData, setFallbackMindMapData] = useState(null);
  const { playPodcast, stopCurrentPodcast } = usePodcastManager();

  const formatPublishDate = (dateString) => {
    const date = new Date(dateString);
    const options = { year: 'numeric', month: language === 'en' ? 'long' : 'long', day: 'numeric' };
    return date.toLocaleDateString(language === 'en' ? 'en-US' : 'it-IT', options);
  };

  const handleMindMapOpen = (resourceLanguage) => {
    const resource = article.resources?.[resourceLanguage]?.mindmap;
    if (!resource) {
      console.log(`❌ Mind map not available for ${resourceLanguage}`);
      return;
    }

    try {
      const newWindow = window.open(resource, '_blank', 'noopener,noreferrer,width=1200,height=800');
      
      if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
        setFallbackMindMapData({
          url: resource,
          title: article.titles[language] || article.titles.en || article.titles.it,
          language: resourceLanguage
        });
        setShowMindMapFallback(true);
      }
    } catch (error) {
      console.error('❌ Error opening mind map:', error);
      setFallbackMindMapData({
        url: resource,
        title: article.titles[language] || article.titles.en || article.titles.it,
        language: resourceLanguage
      });
      setShowMindMapFallback(true);
    }
  };

  const handleAction = (type, resourceLanguage) => {
    const resource = article.resources?.[resourceLanguage]?.[type];
    if (!resource) return;

    if (type === 'mindmap') {
      handleMindMapOpen(resourceLanguage);
      return;
    }

    if (type === 'podcast') {
      if (modalResource) {
        setModalResource(null);
      }
      
      setTimeout(() => {
        const modalElement = document.querySelector('[data-resource-modal="podcast"]');
        playPodcast(resource, article.titles[language] || article.titles.en, modalElement);
      }, 500);
    } else {
      stopCurrentPodcast();
    }
    
    setModalResource({
      type,
      url: resource,
      title: article.titles[language] || article.titles.en || article.titles.it
    });
  };

  const getAvailableResourceLanguages = () => {
    const availableLanguages = [];
    if (article.resources?.en) {
      availableLanguages.push('en');
    }
    if (article.resources?.it) {
      availableLanguages.push('it');
    }
    return availableLanguages;
  };

  const availableLanguages = getAvailableResourceLanguages();

  // ✅ LAYOUT ESATTO DELL'IMMAGINE
  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg shadow-lg p-6 mb-6 w-full"
      >
        {/* ✅ SEZIONE SUPERIORE: Titolo e metadati in una riga */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between mb-4">
          <div className="flex-1">
            <h3 className="text-xl font-bold text-gray-900 mb-2 leading-tight">
              {article.titles[language] || article.titles.en || article.titles.it}
            </h3>
            <div className="flex flex-col sm:flex-row sm:items-center sm:gap-4 text-sm text-gray-600 mb-3">
              <div className="flex items-center">
                <FaUser className="mr-2" />
                <span>{article.authors.join(', ')}</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="mr-2" />
                <span>{formatPublishDate(article.publishDate)}</span>
              </div>
            </div>
          </div>
          
          {/* Badge lingue a destra */}
          <div className="flex flex-row lg:flex-col items-start lg:items-end gap-2 mt-2 lg:mt-0 lg:ml-4">
            {availableLanguages.map(lang => (
              <div key={lang} className="flex items-center px-3 py-1 bg-gray-50 rounded-full border border-gray-200">
                <span className="mr-1 text-sm">{lang === 'en' ? '🇺🇸' : '🇮🇹'}</span>
                <span className="text-xs font-medium text-gray-700">{lang.toUpperCase()}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ✅ SEZIONE CENTRALE: Descrizione */}
        <p className="text-gray-600 text-sm leading-relaxed mb-6">
          {article.descriptions[language] || article.descriptions.en || article.descriptions.it}
        </p>

        {/* ✅ SEZIONE INFERIORE: Blocchi risorse affiancati */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {availableLanguages.map(resourceLanguage => (
            <div key={resourceLanguage} className="bg-gray-50 rounded-lg p-4">
              {/* Intestazione del blocco lingua */}
              <div className="flex items-center justify-center mb-3">
                <span className="text-lg mr-2">{resourceLanguage === 'en' ? '🇺🇸' : '🇮🇹'}</span>
                <span className="font-medium text-gray-900 text-center">
                  {resourceLanguage === 'en' ? 'English' : 'Italiano'}
                </span>
              </div>
              
              {/* ✅ PULSANTI ORIZZONTALI - Come nell'immagine */}
              <div className="flex flex-row gap-2">
                {/* Blog Post Button */}
                {article.resources?.[resourceLanguage]?.blogpost && (
                  <button
                    onClick={() => handleAction('blogpost', resourceLanguage)}
                    className="flex-1 flex items-center justify-center gap-1 px-2 py-2 bg-green-500 hover:bg-green-600 text-white rounded text-xs font-medium transition-colors"
                  >
                    <FaFileAlt className="text-xs" />
                    <span>Blog Post</span>
                  </button>
                )}
                
                {/* Mind Map Button */}
                {article.resources?.[resourceLanguage]?.mindmap && (
                  <button
                    onClick={() => handleAction('mindmap', resourceLanguage)}
                    className="flex-1 flex items-center justify-center gap-1 px-2 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded text-xs font-medium transition-colors"
                  >
                    <FaSitemap className="text-xs" />
                    <span>Mind Map</span>
                  </button>
                )}
                
                {/* Podcast Button */}
                {article.resources?.[resourceLanguage]?.podcast && (
                  <button
                    onClick={() => handleAction('podcast', resourceLanguage)}
                    className="flex-1 flex items-center justify-center gap-1 px-2 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded text-xs font-medium transition-colors"
                  >
                    <FaPodcast className="text-xs" />
                    <span>Podcast</span>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </motion.div>

      <AnimatePresence>
        {modalResource && (
          <ResourceModal
            isOpen={true}
            onClose={() => {
              setModalResource(null);
              if (modalResource.type === 'podcast') {
                stopCurrentPodcast();
              }
            }}
            resource={modalResource}
            language={language}
          />
        )}
        
        {showMindMapFallback && fallbackMindMapData && (
          <MindMapFallbackModal
            isOpen={showMindMapFallback}
            onClose={() => {
              setShowMindMapFallback(false);
              setFallbackMindMapData(null);
            }}
            url={fallbackMindMapData.url}
            title={fallbackMindMapData.title}
            language={fallbackMindMapData.language}
          />
        )}
      </AnimatePresence>
    </>
  );
};

const FreeResources = () => {
  const { t, language } = useLanguage();
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [languageFilter, setLanguageFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('alphabetical-asc');
  const [searchTerm, setSearchTerm] = useState('');

  // ✅ NUOVO: Usa il hook per reset globale dei podcast
  usePodcastReset();

  const handleTopicSelect = (topic) => {
    setSelectedTopic(topic);
    setLanguageFilter('all');
    setSortOrder('alphabetical-asc');
    setSearchTerm('');
  };

  const handleBackToTopics = () => {
    setSelectedTopic(null);
    setLanguageFilter('all');
    setSortOrder('alphabetical-asc');
    setSearchTerm('');
  };

  const getFilteredAndSortedArticles = () => {
    if (!selectedTopic) return [];
    
    let articles = articlesByTopic[selectedTopic.id] || [];

    // Search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      articles = articles.filter(article => {
        const titleMatch = Object.values(article.titles).some(title => 
          title.toLowerCase().includes(searchLower)
        );
        const authorMatch = article.authors.some(author => 
          author.toLowerCase().includes(searchLower)
        );
        return titleMatch || authorMatch;
      });
    }

    // Language filter
    if (languageFilter !== 'all') {
      articles = articles.filter(article => {
        if (!article.availableLanguages.includes(languageFilter)) return false;
        const resources = article.resources?.[languageFilter];
        return resources && (resources.blogpost || resources.mindmap || resources.podcast);
      });
    }

    // Sort
    articles = [...articles].sort((a, b) => {
      switch (sortOrder) {
        case 'alphabetical-asc':
          return (a.titles[language] || a.titles.en || a.titles.it)
            .localeCompare(b.titles[language] || b.titles.en || b.titles.it);
        case 'alphabetical-desc':
          return (b.titles[language] || b.titles.en || b.titles.it)
            .localeCompare(a.titles[language] || a.titles.en || a.titles.it);
        case 'date-newest':
          return new Date(b.publishDate) - new Date(a.publishDate);
        case 'date-oldest':
          return new Date(a.publishDate) - new Date(b.publishDate);
        default:
          return 0;
      }
    });

    return articles;
  };

  const getArticleCounts = () => {
    if (!selectedTopic) return { total: 0, en: 0, it: 0 };
    
    const allArticles = articlesByTopic[selectedTopic.id] || [];
    let totalCount = 0;
    let enCount = 0;
    let itCount = 0;

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      const searchFiltered = allArticles.filter(article => {
        const titleMatch = Object.values(article.titles).some(title => 
          title.toLowerCase().includes(searchLower)
        );
        const authorMatch = article.authors.some(author => 
          author.toLowerCase().includes(searchLower)
        );
        return titleMatch || authorMatch;
      });

      totalCount = searchFiltered.length;
      enCount = searchFiltered.filter(article => {
        if (!article.availableLanguages.includes('en')) return false;
        const resources = article.resources?.en;
        return resources && (resources.blogpost || resources.mindmap || resources.podcast);
      }).length;
      itCount = searchFiltered.filter(article => {
        if (!article.availableLanguages.includes('it')) return false;
        const resources = article.resources?.it;
        return resources && (resources.blogpost || resources.mindmap || resources.podcast);
      }).length;
    } else {
      totalCount = allArticles.length;
      enCount = allArticles.filter(article => {
        if (!article.availableLanguages.includes('en')) return false;
        const resources = article.resources?.en;
        return resources && (resources.blogpost || resources.mindmap || resources.podcast);
      }).length;
      itCount = allArticles.filter(article => {
        if (!article.availableLanguages.includes('it')) return false;
        const resources = article.resources?.it;
        return resources && (resources.blogpost || resources.mindmap || resources.podcast);
      }).length;
    }

    return { total: totalCount, en: enCount, it: itCount };
  };

  const filteredArticles = getFilteredAndSortedArticles();
  const articleCounts = getArticleCounts();

  if (!selectedTopic) {
    return (
      <div className="min-h-screen bg-gray-50 py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <div className="flex flex-col sm:flex-row justify-center gap-4 mb-8">
              <Link
                to="/"
                className="flex items-center text-green-600 hover:text-green-800 font-semibold transition-colors bg-white px-4 py-2 rounded-full shadow-md hover:shadow-lg"
              >
                <FaHome className="mr-2" />
                {t('backToHome')}
              </Link>
              <Link
                to="/courses"
                className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white font-semibold px-4 py-2 rounded-full shadow-md hover:shadow-lg transition-colors"
              >
                <FaGraduationCap />
                <span>{t('viewPremiumCourses')}</span>
              </Link>
            </div>

            <h1 className="text-5xl font-bold text-gray-900 mb-6">{t('freeResourcesTitle')}</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">{t('freeResourcesSubtitle')}</p>

            <div className="bg-blue-50 border-l-4 border-blue-400 p-6 rounded-r-lg max-w-4xl mx-auto text-left mb-12">
              <div className="flex items-start">
                <FaCoffee className="text-blue-600 mr-3 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-bold text-blue-900 mb-2">{t('howToUse')}</h3>
                  <p className="text-blue-800 leading-relaxed">{t('resourceInstructions')}</p>
                </div>
              </div>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {topics.map((topic, index) => (
              <motion.div
                key={topic.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
              >
                <TopicCard topic={topic} onSelect={handleTopicSelect} />
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center space-x-4 mb-6">
            <button
              onClick={handleBackToTopics}
              className="flex items-center text-indigo-600 hover:text-indigo-800 font-semibold transition-colors"
            >
              <FaArrowLeft className="mr-2" />
              {t('backToTopics')}
            </button>
            <Link
              to="/"
              className="flex items-center text-green-600 hover:text-green-800 font-semibold transition-colors"
            >
              <FaHome className="mr-2" />
              {t('backToHome')}
            </Link>
          </div>

          <div className="flex items-center mb-6">
            <div className={`w-16 h-16 ${selectedTopic.color} rounded-full flex items-center justify-center mr-6`}>
              <selectedTopic.icon className="text-white text-2xl" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">{t(selectedTopic.titleKey)}</h1>
              <p className="text-xl text-gray-600">{t(selectedTopic.descriptionKey)}</p>
            </div>
          </div>
        </motion.div>

        <SearchInput 
          searchTerm={searchTerm} 
          onSearchChange={setSearchTerm}
          onClearSearch={() => setSearchTerm('')}
          resultsCount={filteredArticles.length}
        />

        <LanguageFilter 
          selectedFilter={languageFilter} 
          onFilterChange={setLanguageFilter}
          articleCounts={articleCounts}
        />

        <SortFilter 
          selectedSort={sortOrder} 
          onSortChange={setSortOrder}
        />

        {filteredArticles.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📚</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('noArticlesFound')}</h3>
            <p className="text-gray-600">{t('noArticlesInLanguage')}</p>
          </div>
        ) : (
          /* ✅ AGGIORNATO: Layout compatto per le card degli articoli */
          <div className="space-y-4">
            {filteredArticles.map(article => (
              <ArticleCard key={article.id} article={article} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FreeResources;